import { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  ClientSideRowModelModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/* ── helpers ───────────────────────────────────────── */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return "";
  return path.split(".").reduce((acc, part) => (acc && acc[part] != null ? acc[part] : ""), obj);
};

const computeCellValue = (colDef, rowData, node) => {
  let value = "";

  if (colDef.valueGetter) {
    try {
      value = colDef.valueGetter({ data: rowData, node });
    } catch {
      value = "";
    }
  } else if (colDef.field?.includes(".")) {
    value = getNestedValue(rowData, colDef.field);
  } else if (colDef.field) {
    value = rowData[colDef.field];
  }

  // Apply AG Grid valueFormatter
  if (colDef.valueFormatter) {
    try {
      value = colDef.valueFormatter({
        value,
        data: rowData,
        node,
        colDef,
      });
    } catch { }
  }

  // Arrays
  if (Array.isArray(value)) {
    value = value
      .map(
        item =>
          item?.memberId?.name ||
          item?.name ||
          item?.disbursedAmount ||
          (typeof item === "object"
            ? JSON.stringify(item)
            : item)
      )
      .join(", ");
  }

  // Mongo Decimal128
  if (
    value &&
    typeof value === "object" &&
    value.$numberDecimal !== undefined
  ) {
    value = value.$numberDecimal;
  }

  // Any remaining object
  if (value && typeof value === "object") {
    value = JSON.stringify(value);
  }

  // ISO Date
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T/.test(value)
  ) {
    value = new Date(value).toLocaleDateString("en-IN");
  }

  return value ?? "";
};

/* ── TableComponent ────────────────────────────────── */
const TableComponent = ({ data, columns }) => {
  const [gridApi, setGridApi] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(
    new Set(
      columns
        .filter((c) => c.hideByDefault)
        .map((c) => c.field || c.headerName)
    )
  );
  const exportRef = useRef(null);
  const colsRef = useRef(null);

  useEffect(() => { setFilteredData(data || []); }, [data]);

  /* close dropdowns on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
      if (colsRef.current && !colsRef.current.contains(e.target)) setColsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Search ── */
  const handleSearch = (e) => {
    const v = e.target.value.toLowerCase();
    setSearchText(v);
    if (!v) { setFilteredData(data || []); return; }
    setFilteredData((data || []).filter(row =>
      columns.some(col => {
        if (col.valueGetter) {
          try { return String(col.valueGetter({ data: row }) ?? "").toLowerCase().includes(v); } catch { }
        }
        return String(col.field ? getNestedValue(row, col.field) : "").toLowerCase().includes(v);
      })
    ));
  };

  /* ── Column visibility ── */
  const toggleColumn = (col) => {
    const key = col.field || col.headerName;
    const next = new Set(hiddenCols);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setHiddenCols(next);
    if (gridApi) {
      try { gridApi.setColumnsVisible([key], !next.has(key)); } catch { }
    }
  };

  const showAllColumns = () => {
    if (gridApi && hiddenCols.size > 0) {
      try { gridApi.setColumnsVisible([...hiddenCols], true); } catch { }
    }
    setHiddenCols(new Set());
  };

  /* ── Export helpers ── */
  const getExportRows = () => {
    const visibleCols = columns.filter(
      (c) => c.field !== "action" && !hiddenCols.has(c.field || c.headerName)
    );

    if (gridApi) {
      const rows = [];

      try {
        gridApi.forEachNodeAfterFilterAndSort((node) => {
          if (node?.data) {
            rows.push({
              data: node.data,
              node,
            });
          }
        });
      } catch { }

      if (rows.length) {
        return { visibleCols, rows };
      }
    }

    return {
      visibleCols,
      rows: (filteredData || []).map((data, index) => ({
        data,
        node: {
          rowIndex: index,
        },
      })),
    };
  };

  const exportExcel = () => {
    const { visibleCols, rows } = getExportRows();

    const exportData = rows.map(({ data, node }) => {
      const obj = {};

      visibleCols.forEach((col) => {
        obj[col.headerName] = computeCellValue(col, data, node);
      });

      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, "export.xlsx");

    setExportOpen(false);
  };

  const exportPDF = () => {
    const { visibleCols, rows } = getExportRows();

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(11);
    doc.text("Exported Data", 40, 30);

    doc.autoTable({
      head: [visibleCols.map((c) => c.headerName)],
      body: rows.map(({ data, node }) =>
        visibleCols.map((col) =>
          String(computeCellValue(col, data, node) ?? "")
        )
      ),
      startY: 44,
      styles: {
        fontSize: 7,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [27, 94, 32],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 251, 245],
      },
      margin: {
        left: 20,
        right: 20,
      },
    });

    doc.save("export.pdf");
    setExportOpen(false);
  };

  const handlePrint = () => {
    const { visibleCols, rows } = getExportRows();
    const headers = visibleCols.map(c => `<th style="background:#1B5E20;color:#fff;padding:6px 10px;font-size:11px;white-space:nowrap">${c.headerName}</th>`).join("");
    const bodyRows = rows.map(({ data, node }, i) => {
      const cells = visibleCols.map(col => `<td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #eee">${computeCellValue(col, data, node) ?? ""}</td>`).join("");
      return `<tr style="background:${i % 2 === 0 ? "#fff" : "#F1F8F1"}">${cells}</tr>`;
    }).join("");
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>Print</title>
      <style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse}@media print{@page{margin:10mm}}</style>
      </head><body>
      <h3 style="color:#1B5E20;margin-bottom:12px">Data Export</h3>
      <table><thead><tr>${headers}</tr></thead><tbody>${bodyRows}</tbody></table>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
    setExportOpen(false);
  };

  /* ── Grid callbacks ── */
  const onGridReady = (params) => {
    setGridApi(params.api);

    // Apply default hidden columns
    if (hiddenCols.size > 0) {
      params.api.setColumnsVisible([...hiddenCols], false);
    }

    setTimeout(() => {
      try {
        params.api.autoSizeAllColumns(false);
      } catch { }
    }, 100);
  };

  const onFirstDataRendered = (params) => { params.api.sizeColumnsToFit(); };

  /* ── Style helpers ── */
  const dropBtnStyle = (active) => ({
    display: "flex", alignItems: "center", gap: 6,
    padding: "7px 14px", border: "1.5px solid " + (active ? "#A5D6A7" : "#E2E8F0"), borderRadius: 8,
    background: active ? "#E8F5E9" : "#fff", color: active ? "#1B5E20" : "#4A5568",
    fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)", transition: "all 0.15s",
  });

  const dropMenuStyle = {
    position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 9999,
    background: "#fff", borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,0.14)",
    border: "1px solid #E2E8F0", minWidth: 190, overflow: "hidden",
  };

  const dropItem = (hoverBg = "#F7FAFC") => ({
    onMouseEnter: (e) => { e.currentTarget.style.background = hoverBg; },
    onMouseLeave: (e) => { e.currentTarget.style.background = "#fff"; },
  });

  const togglableCols = columns.filter(c => c.field && c.field !== "action");

  return (
    <div>
      {/* ── Premium Toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>

        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 320 }}>
          <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#A0AEC0", fontSize: 16 }} />
          <input
            type="text"
            value={searchText}
            onChange={handleSearch}
            placeholder="Search records..."
            style={{ width: "100%", padding: "7px 12px 7px 34px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
            onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
            onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
          />
          {searchText && (
            <button onClick={() => { setSearchText(""); setFilteredData(data || []); }}
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#A0AEC0", fontSize: 14, padding: 0 }}>
              <i className="mdi mdi-close-circle" />
            </button>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Record count */}
        {filteredData.length > 0 && (
          <span style={{ fontSize: 11, color: "#718096", background: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "4px 10px", fontWeight: 600 }}>
            {filteredData.length}{searchText ? ` of ${(data || []).length}` : ""} records
          </span>
        )}

        {/* Export dropdown */}
        <div ref={exportRef} style={{ position: "relative" }}>
          <button style={dropBtnStyle(exportOpen)} onClick={() => { setExportOpen(!exportOpen); setColsOpen(false); }}>
            <i className="mdi mdi-export-variant" style={{ fontSize: 15 }} />
            Export
            <i className={`mdi mdi-chevron-${exportOpen ? "up" : "down"}`} style={{ fontSize: 13, opacity: 0.6 }} />
          </button>
          {exportOpen && (
            <div style={dropMenuStyle}>
              <div style={{ padding: "8px 14px 4px", fontSize: 10, fontWeight: 700, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.6px" }}>Export as</div>
              <button style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: 13, background: "#fff", fontWeight: 500, color: "#2D3748" }}
                {...dropItem("#F0FFF4")} onClick={exportExcel}>
                <i className="mdi mdi-microsoft-excel" style={{ fontSize: 18, color: "#217346" }} /> Excel (.xlsx)
              </button>
              <button style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: 13, background: "#fff", fontWeight: 500, color: "#2D3748" }}
                {...dropItem("#FFF5F5")} onClick={exportPDF}>
                <i className="mdi mdi-file-pdf-box" style={{ fontSize: 18, color: "#D32F2F" }} /> PDF (.pdf)
              </button>
              <div style={{ height: 1, background: "#EDF2F7", margin: "4px 0" }} />
              <button style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: 13, background: "#fff", fontWeight: 500, color: "#2D3748" }}
                {...dropItem("#EBF8FF")} onClick={handlePrint}>
                <i className="mdi mdi-printer" style={{ fontSize: 18, color: "#1565C0" }} /> Print
              </button>
            </div>
          )}
        </div>

        {/* Columns toggle */}
        <div ref={colsRef} style={{ position: "relative" }}>
          <button style={dropBtnStyle(colsOpen || hiddenCols.size > 0)} onClick={() => { setColsOpen(!colsOpen); setExportOpen(false); }}>
            <i className="mdi mdi-view-column-outline" style={{ fontSize: 15 }} />
            Columns
            {hiddenCols.size > 0 && (
              <span style={{ background: "#E53E3E", color: "#fff", borderRadius: "50rem", fontSize: 10, fontWeight: 700, padding: "1px 6px", minWidth: 18, textAlign: "center" }}>
                {hiddenCols.size}
              </span>
            )}
            <i className={`mdi mdi-chevron-${colsOpen ? "up" : "down"}`} style={{ fontSize: 13, opacity: 0.6 }} />
          </button>
          {colsOpen && (
            <div style={{ ...dropMenuStyle, left: "auto", right: 0, maxHeight: 360, overflowY: "auto", minWidth: 220 }}>
              <div style={{ padding: "8px 14px 4px", fontSize: 10, fontWeight: 700, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Toggle Columns</span>
                {hiddenCols.size > 0 && (
                  <button onClick={showAllColumns} style={{ background: "none", border: "none", cursor: "pointer", color: "#2E7D32", fontSize: 10, fontWeight: 700, padding: 0 }}>
                    Show all
                  </button>
                )}
              </div>
              {togglableCols.map((col) => {
                const key = col.field || col.headerName;
                const hidden = hiddenCols.has(key);
                return (
                  <label key={key}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 14px", cursor: "pointer", fontSize: 12, color: hidden ? "#A0AEC0" : "#2D3748", fontWeight: hidden ? 400 : 500, background: "#fff" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#F7FAFC"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                  >
                    <input type="checkbox" checked={!hidden} onChange={() => toggleColumn(col)} style={{ accentColor: "#2E7D32", width: 14, height: 14 }} />
                    {col.headerName}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── AG Grid ── */}
      <div className="ag-theme-alpine" style={{ width: "100%", height: "auto" }}>
        {filteredData.length > 0 ? (
          <AgGridReact
            domLayout="autoHeight"
            columnDefs={columns}
            rowData={filteredData}
            rowSelection={{ type: "multiple" }}
            pagination={true}
            paginationPageSize={25}
            defaultColDef={{ sortable: true, filter: "agTextColumnFilter", resizable: true, minWidth: 100 }}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#A0AEC0", fontSize: 13 }}>
            <i className="mdi mdi-table-off" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
            No data available
          </div>
        )}
      </div>
    </div>
  );
};

export default TableComponent;
